import type { CSSProperties } from 'react';
import { Cpu, MemoryStick, HardDrive, DatabaseBackup } from 'lucide-react';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { UsageMeter } from './UsageMeter';
import type { ZoneCapacity, CapacityValue } from '@/api/opennebula/zones';

/**
 * Four capacity tiles — CPU / Memory / Datastore / Backup — each showing
 * used/total with a utilization bar. Datastore aggregates SYSTEM+IMAGE+FILE
 * pools; Backup is the separate BACKUP storage.
 */
interface Props {
  capacity: ZoneCapacity;
}

const ZERO: CapacityValue = { total: 0, used: 0 };

const tiles = [
  { key: 'cpu', label: 'CPU', icon: Cpu, unit: ' cores' },
  { key: 'memory', label: 'Memory', icon: MemoryStick, unit: ' GB' },
  { key: 'datastore', label: 'Datastore', icon: HardDrive, unit: ' GB' },
  { key: 'backup', label: 'Backup', icon: DatabaseBackup, unit: ' GB' },
] as const;

const head: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: 'var(--vn-fg-muted)',
  fontSize: 'var(--vn-font-size-sm)',
  marginBottom: 6,
};

export function CapacityMeters({ capacity }: Props) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--vn-space-3)',
      }}
    >
      {tiles.map(({ key, label, icon: Icon, unit }) => {
        const v = capacity[key] ?? ZERO;
        return (
          <Card key={key} flat>
            <CardBody>
              <div style={head}>
                <Icon size={16} /> {label}
              </div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {v.used.toLocaleString()}
                {v.unit ?? unit} / {v.total.toLocaleString()}
                {v.unit ?? unit}
              </div>
              <UsageMeter used={v.used} total={v.total} barOnly />
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}
